export interface TopicData {
  media: string
  //TODO: Fix this use of `any`
  // color: string | number | { r: number; g: number; b: number }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  color: any
  id: number
  name: string
  value: string
}

export interface AudienceData extends TopicData {
  singularTerm: string
}

/**
 * Composable function returns helpers to fetch data sources from Storyblok
 *
 * @returns {Object} - Object containing functions to fetch data sources
 */
export default function useStoryblokDataSources() {
  const { fetchAsyncData } = useStoryblokHelpers()

  // Fetch all audiences dimensions
  const fetchAudiences = () =>
    useAsyncData(
      'audiences',
      () => {
        console.log('Fetching data source: audiences')
        return Promise.all([
          fetchAsyncData<undefined, ISbDatasourceEntries>(
            'datasource_entries',
            {
              datasource: 'audiences',
              dimension: 'media',
              per_page: 1000,
            },
          ),
          fetchAsyncData<undefined, ISbDatasourceEntries>(
            'datasource_entries',
            {
              datasource: 'audiences',
              dimension: 'weighting',
              per_page: 1000,
            },
          ),
          fetchAsyncData<undefined, ISbDatasourceEntries>(
            'datasource_entries',
            {
              datasource: 'audiences',
              dimension: 'singular_term',
              per_page: 1000,
            },
          ),
        ])
      },
      {
        dedupe: 'defer',
        transform: ([
          { data: media },
          { data: weighting },
          { data: singularTerm },
        ]): AudienceData[] =>
          media.value.data.datasource_entries.map((item, index) => ({
            media: item.dimension_value,
            color:
              weighting.value.data.datasource_entries?.[index]
                ?.dimension_value || 0,
            singularTerm:
              singularTerm.value.data.datasource_entries?.[index]
                ?.dimension_value || item.name,
            id: item.id,
            name: item.name,
            value: item.value,
          })),
        getCachedData: getCachedDataDefault,
      },
    )

  // Fetch all topics dimensions
  const fetchTopics = () =>
    useAsyncData(
      'topics',
      () => {
        console.log('Fetching data source: topics')
        return Promise.all([
          fetchAsyncData<undefined, ISbDatasourceEntries>(
            'datasource_entries',
            {
              datasource: 'topics',
              dimension: 'media',
              per_page: 1000,
            },
          ),
          fetchAsyncData<undefined, ISbDatasourceEntries>(
            'datasource_entries',
            {
              datasource: 'topics',
              dimension: 'weighting',
              per_page: 1000,
            },
          ),
        ])
      },
      {
        dedupe: 'defer',
        transform: ([{ data: media }, { data: weighting }]): TopicData[] =>
          media.value.data.datasource_entries.map((item, index) => ({
            media: item.dimension_value,
            color:
              weighting.value.data.datasource_entries?.[index]
                ?.dimension_value || 0,
            id: item.id,
            name: item.name,
            value: item.value,
          })),
        getCachedData: getCachedDataDefault,
      },
    )

  // Fetch all states
  const fetchStates = () =>
    useAsyncData(
      'states',
      () => {
        console.log('Fetching data source: states')
        return fetchAsyncData<undefined, ISbDatasourceEntries>(
          'datasource_entries',
          {
            datasource: 'states',
            per_page: 1000,
          },
        )
      },
      {
        dedupe: 'defer',
        transform: datasourceEntryTransformDefault,
        getCachedData: getCachedDataDefault,
      },
    )

  // Fetch all resource types
  const fetchResourceTypes = () => {
    console.log('Fetching data source: resource types')
    return fetchAsyncData<DatasourceEntry[], ISbDatasourceEntries>(
      'datasource_entries',
      {
        datasource: 'resource-type',
        per_page: 1000,
      },
      {
        dedupe: 'defer',
        server: false,
        transform: (response) => {
          console.log(
            'Fetched resource types',
            unref(response.data).datasource_entries,
          )
          return response
        },
        getCachedData: getCachedDataDefault,
      },
    )
  }

  // Fetch all service types
  const fetchServiceTypes = () =>
    useAsyncData(
      'service-types',
      () => {
        console.log('Fetching data source: service types')
        return fetchAsyncData<undefined, ISbDatasourceEntries>(
          'datasource_entries',
          {
            datasource: 'service-type',
            per_page: 1000,
          },
        )
      },
      {
        dedupe: 'defer',
        transform: datasourceEntryTransformDefault,
        getCachedData: getCachedDataDefault,
      },
    )

  return {
    fetchAudiences,
    fetchTopics,
    fetchStates,
    fetchResourceTypes,
    fetchServiceTypes,
  }
}
